import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import URLs from '../../../../../utils/appURLs';
import { updateToast } from '../../../../../common/redux/reducer/commonSlices/toastSlice';
import axiosInstance from '../../../../../app/config/axiosInstance';
import { getVehicleSingleDetailsAction } from './vehicleList';

const serviceContext = process.env.REACT_APP_SERVICE_CONTEXT;

type ReturnedType = { data: [] }; // The type of the return of the thunk
type ThunkArg = { vehicleNumber: string; nonFreeWay: string; freeWay: string };

interface GetDataType {
  id: number;
  overspeed: number;
  rcExpiryDate: Date | null;
  vehicleModel: string | null;
  vehicleNumber: string;
}
interface OverSpeedState {
  isLoading: boolean;
  data: GetDataType[] | any;
  error: string | null;
}

const initialState: OverSpeedState = {
  isLoading: false,
  data: null,
  error: null
};

// Action function to get overspeed details:
export const getOverSpeedAction = createAsyncThunk<AxiosResponse<GetDataType[]>>(
  'overSpeed/get',
  async (_, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${serviceContext}${URLs.GET_OVERSPEED_DETAILS}`
      });
      return response?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// Action function to get vehicles details:
export const getAllVehiclesAction = createAsyncThunk<AxiosResponse<any>, string>(
  'vehicles/get',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.GET_VEHICLES_DETAILS}vehicleCategory=${data}`
      });
      return response?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

// Action function to update user details:
export const updateOverSpeedAction = createAsyncThunk<ReturnedType, ThunkArg>(
  'overSpeed/update',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'PUT',
        url: `${serviceContext}${URLs.UPDATE_OVERSPEED_DETAILS}deviceid=${data.vehicleNumber}&freewaySpeedLimit=${data.freeWay}&nonFreewaySpeedLimit=${data.nonFreeWay}`
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: 'OverSpeed updated successfully',
          severity: 'success'
        })
      );
      return response;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// update yaantrac vehicle
export const updateYaantracVehicle = createAsyncThunk<ReturnedType, any>(
  'update/yaantracVehicle',
  async ({ data, category }, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'PUT',
        url: `${URLs.UPDATE_YAANTRAC_VEHICLE}`,
        data: data.updatePayload
      });
      // await thunkApi.dispatch(
      //   getVehicleSingleDetailsAction(data?.updatePayload?.vehicleNumber)
      // );
      await thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message
            ? error?.response?.data?.message
            : error?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// update yaantrac vehicle slice
const updateYaantracSlice = createSlice({
  name: 'updateYaantracVehicle',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(updateYaantracVehicle.pending, state => {
      state.isLoading = true;
      state.data = [];
    });
    builder.addCase(
      updateYaantracVehicle.fulfilled,
      (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.data = action.payload;
        state.error = null;
      }
    );
    builder.addCase(updateYaantracVehicle.rejected, (state, action: any) => {
      state.isLoading = false;
      state.error = action?.payload?.response?.data?.error || 'Something went wrong';
    });
  }
});

// Function to update user details:
const overSpeedSlice = createSlice({
  name: 'overSpeedDetails',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      // Get OverSpeed:
      .addCase(getOverSpeedAction.pending, state => {
        state.isLoading = true;
        state.data = [];
        state.error = null;
      })
      .addCase(
        getOverSpeedAction.fulfilled,
        (state, action: PayloadAction<AxiosResponse>) => {
          state.isLoading = false;
          state.data = action.payload.data.overspeedDetails.map(
            (item: any, index: number) => ({
              ...item,
              overSpeedID: index + 1
            })
          );
          state.error = null;
        }
      )
      .addCase(getOverSpeedAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.data = [];
        state.error = action.payload.response?.data?.error;
      })

      // Update overSpeed:
      .addCase(updateOverSpeedAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        updateOverSpeedAction.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          state.isLoading = false;
          state.data = action.payload.data;
          state.error = null;
        }
      )
      .addCase(updateOverSpeedAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload.response?.data?.error;
      });
  }
});

// Slice for vehicles get
const getVechilesSlice = createSlice({
  name: 'agent',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getAllVehiclesAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAllVehiclesAction.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.data = action.payload?.data;
        state.error = null;
      })
      .addCase(getAllVehiclesAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error;
      });
  }
});

export default overSpeedSlice.reducer;
export const getVechiles = getVechilesSlice.reducer;
export const yaantracVehicleReducer = updateYaantracSlice.reducer;
